import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  CardFooter,
  CardHeader,
  Container,
  Row,
  Table,
  Input,
  FormGroup,
  Col,
  Label,
} from "reactstrap";

import { helper } from "lib/helper";
import TablePagination from "views/components/TablePagination";
import ExportButton from "components/ExportButton";
import adminModel from "models/admin.model";
import tagsModel from "models/tags.model";
import Loader from "components/loader/Loader";

const TagRequest = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [categoriesList, setCategoriesList] = useState([]);
  const [experienceList, setExperienceList] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [expid, setExpid] = useState(""); // initially empty

  const navigate = useNavigate();

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const getExperienceCategory = async () => {
    setIsLoading(true);
    try {
      const res = await adminModel.getExperienceCategory();
      setExperienceList(res.data);
      if (!expid) {
        setExpid(res.data[0]?._id); // set first category as default
      }

      const response = await tagsModel.getAlltags();
      setCategoriesList(response.data.reverse());
    } catch (error) {
      helper.sweetalert.toast(error.response?.data?.message, "warning");
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    helper.sweetalert
      .confirm("Are you sure you want to delete this tag?", "info", true)
      .then(async (result) => {
        if (result.isConfirmed) {
          setIsLoading(true);
          try {
            await tagsModel.deleteTagRequest(id);
            helper.sweetalert.toast("Tag deleted successfully!");
            getExperienceCategory();
          } catch (error) {
            helper.sweetalert.toast(
              error.response?.data?.message || "Something went wrong",
              "warning"
            );
          } finally {
            setIsLoading(false);
          }
        }
      });
  };

  const addTagHandler = async (item) => {
    const payload = {
      categoryId: item.categoryId,
      fieldName: item.optionName,
      tagValue: item.tag,
    };

    helper.sweetalert
      .confirm(`Are you sure you want to approve tag: "${item.tag}"?`, "info", true)
      .then(async (result) => {
        if (result.isConfirmed) {
          try {
            setIsLoading(true);
            const res = await tagsModel.addTagToExperienceOptions(payload);
            helper.sweetalert.toast("Tag approved and added successfully!");
            getExperienceCategory(); // Refresh the list after update
          } catch (error) {
            helper.sweetalert.toast(
              error.response?.data?.message || "Failed to approve tag",
              "warning"
            );
          } finally {
            setIsLoading(false);
          }
        }
      });
  };

  const getCategoryName = (id) => {
    const match = experienceList.find((cat) => cat._id === id);
    return match ? match.name : id;
  };

  const filteredCategories = categoriesList.filter(
    (item) =>
      item.categoryId === expid &&
      (item?.optionName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item?.tag?.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  useEffect(() => {
    getExperienceCategory();
  }, []);

  return isLoading ? (
    <Loader />
  ) : (
    <Container className="pt-6">
      <Row>
        <div className="col pl-0 pr-0 mr-3">
          <Card className="shadow">
            <CardHeader className="border-0 d-flex justify-content-between align-items-center">
              <h3 className="mb-0">All Request Tags</h3>
              <div className="d-flex align-items-end  gap-2">
                <FormGroup className="mb-0 me-5">
                  <Label className="text-xs" for="type">
                    Category Tags
                  </Label>
                  <Input
                    type="select"
                    name="type"
                    id="type"
                    value={expid}
                    onChange={(e) => setExpid(e.target.value)}
                  >
                    {experienceList.map((e, i) => (
                      <option key={i} value={e._id}>
                        {e.name}
                      </option>
                    ))}
                  </Input>
                </FormGroup>
                <Input
                  type="text"
                  className="mx-3"
                  placeholder="Search by Option Name or Tag..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ width: "300px" }}
                />
                <ExportButton
                  data={filteredCategories}
                  columns={[
                    { label: "S.No", getValue: (_, i) => i + 1 },
                    { label: "Option Name", getValue: (item) => item.optionName },
                    { label: "Tag", getValue: (item) => item.tag },
                    { label: "Category", getValue: (item) => getCategoryName(item.categoryId) },
                    {
                      label: "Created At",
                      getValue: (item) => new Date(item.createdAt).toLocaleString(),
                    },
                  ]}
                />
              </div>
            </CardHeader>
            <Table className="align-items-center table-flush" responsive>
              <thead className="thead-light">
                <tr>
                  <th>S.No</th>
                  <th>Option Name</th>
                  <th>Tag</th>
                  <th>Category</th>
                  <th>Request At</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredCategories
                  .slice((currentPage - 1) * perPage, currentPage * perPage)
                  .map((item, index) => (
                    <tr key={item._id}>
                      <td>{(currentPage - 1) * perPage + index + 1}</td>
                      <td>{item.optionName}</td>
                      <td>{item.tag}</td>
                      <td>{getCategoryName(item.categoryId)}</td>
                      <td>{new Date(item.createdAt).toLocaleString()}</td>
                      <td>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => addTagHandler(item)}
                        >
                          <i className="fa-solid fa-plus"></i>
                        </Button>
                        <Button
                          color="danger"
                          size="sm"
                          onClick={() => handleDelete(item._id)}
                        >
                          <i className="fa-solid fa-trash"></i>
                        </Button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </Table>
            <CardFooter className="py-4">
              <TablePagination
                totalPages={Math.ceil(filteredCategories.length / perPage)}
                currentPage={currentPage}
                onPageChange={handlePageChange}
                pageUrl="/admin/categories?"
              />
            </CardFooter>
          </Card>
        </div>
      </Row>
    </Container>
  );
};

export default TagRequest;
